import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import Image from 'next/image'
import { useState } from 'react'
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Mark } from '@/components/generals/mark'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'
import { SectionLabel } from '@/components/generals/section-label'

interface TestimonialsSectionProps {
    testimonials: Testimonial[]
    label?: string
}

const TestimonialsSection: FC<TestimonialsSectionProps> = ({ testimonials, label = 'Client stories / 05' }): ReactNode => {
    const [current, setCurrent] = useState(0)

    if (!testimonials.length) return null

    const total = testimonials.length
    const testimonial = testimonials[current] ?? testimonials[0]
    const go = (direction: number) => setCurrent((value) => (value + direction + total) % total)

    return (
        <section id='client-stories' aria-labelledby='testimonials-title' data-nav-theme='light' className='relative overflow-hidden bg-stone text-charcoal'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                <div className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div data-reveal className='lg:col-span-4'>
                        <SectionLabel>{label}</SectionLabel>
                        <h2 id='testimonials-title' className='mt-6 font-heading type-section'>
                            Good work <Mark>speaks</Mark> for itself.
                        </h2>

                        <div className='mt-10 hidden items-center gap-3 lg:flex'>
                            <button
                                type='button'
                                onClick={() => go(-1)}
                                aria-label='Previous testimonial'
                                className='flex size-14 items-center justify-center rounded-sm border border-charcoal transition-colors hover:bg-charcoal hover:text-cream focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal'
                            >
                                <ArrowLeft aria-hidden='true' className='size-5' />
                            </button>
                            <button
                                type='button'
                                onClick={() => go(1)}
                                aria-label='Next testimonial'
                                className='flex size-14 items-center justify-center rounded-sm bg-charcoal text-cream transition-colors hover:bg-coral hover:text-charcoal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal'
                            >
                                <ArrowRight aria-hidden='true' className='size-5' />
                            </button>
                        </div>
                    </div>

                    <div
                        data-reveal
                        role='region'
                        aria-roledescription='carousel'
                        aria-label='Client testimonials'
                        className='relative lg:col-span-8'
                        onKeyDown={(event) => {
                            if (event.key === 'ArrowLeft') go(-1)
                            if (event.key === 'ArrowRight') go(1)
                        }}
                    >
                        <div aria-live='polite' className='relative border-t border-charcoal pt-12 md:pt-14'>
                            <span aria-hidden='true' className='absolute left-0 top-0 flex size-11 -translate-y-1/2 items-center justify-center bg-charcoal text-coral'>
                                <Quote className='size-5 fill-current' />
                            </span>
                            <figure key={testimonial.id} className='animate-in fade-in slide-in-from-right-4 duration-500'>
                                <blockquote>
                                    <p className='font-heading text-[clamp(1.4rem,2.4vw,2.15rem)] font-semibold leading-[1.22] tracking-[-0.02em]'>
                                        {testimonial.quote}
                                    </p>
                                </blockquote>

                                <figcaption className='mt-12 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
                                    <div className='flex items-center gap-4'>
                                        {testimonial.logo ? (
                                            <span className='relative size-14 shrink-0 overflow-hidden rounded-sm bg-cream'>
                                                <Image src={testimonial.logo} alt={testimonial.logoAlt} fill sizes='56px' className='object-contain p-2' />
                                            </span>
                                        ) : (
                                            <span aria-hidden='true' className='flex size-14 shrink-0 items-center justify-center rounded-sm bg-charcoal font-heading text-lg font-black text-cream'>
                                                {testimonial.company.slice(0, 1) || 'B'}
                                            </span>
                                        )}
                                        <div>
                                            <p className='font-heading font-wide text-base font-black uppercase'>{testimonial.name}</p>
                                            <p className='mt-1 text-sm text-charcoal/80'>
                                                {[testimonial.role, testimonial.company].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    </div>
                                    {testimonial.isPlaceholder && <PlaceholderTag>Sample testimonial</PlaceholderTag>}
                                </figcaption>
                            </figure>
                        </div>

                        <div className='mt-12 flex items-center justify-between gap-6'>
                            <p className='font-heading font-wide text-3xl font-black tracking-[-0.03em]'>
                                {pad(current + 1)}
                                <span className='text-charcoal/50'> / {pad(total)}</span>
                            </p>

                            <div className='flex flex-1 gap-1.5 sm:max-w-xs'>
                                {testimonials.map((item, index) => (
                                    <button
                                        key={item.id}
                                        type='button'
                                        onClick={() => setCurrent(index)}
                                        aria-label={`Show testimonial ${index + 1}`}
                                        aria-current={index === current ? 'true' : undefined}
                                        className='group flex h-6 flex-1 items-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal'
                                    >
                                        <span className={cn('h-0.5 w-full transition-colors duration-300', index === current ? 'bg-charcoal' : 'bg-charcoal/25 group-hover:bg-charcoal/60')} />
                                    </button>
                                ))}
                            </div>

                            <div className='flex gap-2 lg:hidden'>
                                <button type='button' onClick={() => go(-1)} aria-label='Previous testimonial' className='flex size-12 items-center justify-center rounded-sm border border-charcoal'>
                                    <ArrowLeft aria-hidden='true' className='size-5' />
                                </button>
                                <button type='button' onClick={() => go(1)} aria-label='Next testimonial' className='flex size-12 items-center justify-center rounded-sm bg-charcoal text-cream'>
                                    <ArrowRight aria-hidden='true' className='size-5' />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default TestimonialsSection
